import React, { useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { ApprovalStage, ApprovalStageStatus } from '../../services/approvalService';

// Shared approval-stages list rendered inside entity detail drawers
// (Submittal, RFI, Punch Item). The drawer that embeds this component
// owns the stage loading + onDecision logic (which calls approveStage /
// rejectStage and then reloads and optionally auto-advances the parent
// entity's status).
export interface ApprovalStagesSectionProps {
  pca: IPublicClientApplication;
  stages: ApprovalStage[] | null;
  loading: boolean;
  myOid: string | undefined;
  onDecision: (stage: ApprovalStage, decision: 'approve' | 'reject', comments?: string) => Promise<void> | void;
  /** The label for the parent entity (e.g. "submittal", "RFI", "punch item") used in the empty-state prompt. */
  entityLabel?: string;
  /** The label for the trigger status change (e.g. "Submitted", "In Review", "Ready For Inspection"). */
  triggerStatusLabel?: string;
}

export default function ApprovalStagesSection({
  pca: _pca, stages, loading, myOid, onDecision,
  entityLabel = 'item', triggerStatusLabel = 'Submitted',
}: ApprovalStagesSectionProps) {
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComments, setRejectComments] = useState('');
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [approveComments, setApproveComments] = useState('');

  return (
    <div style={{ marginTop: 20, padding: 12, background: '#F5F6F8', borderRadius: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
        Approval Progress
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: '#9198A8' }}>Loading…</div>
      ) : !stages || stages.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9198A8' }}>
          No approval chain has been instantiated for this {entityLabel}. Changing status to <strong>{triggerStatusLabel}</strong> will
          trigger the project's approval workflow (if configured in Project Settings → Approval Workflows).
        </div>
      ) : (
        <div>
          {stages.slice().sort((a, b) => (a.dagrun_pm_stageorder ?? 0) - (b.dagrun_pm_stageorder ?? 0)).map(s => {
            const status = s.dagrun_pm_status ?? ApprovalStageStatus.Pending;
            const isMyStage = !!myOid && s.dagrun_pm_approverid === myOid && status === ApprovalStageStatus.Pending;
            return (
              <div key={s.dagrun_pm_approvalstageid} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '8px 0', borderBottom: '0.5px solid #EAECF0',
              }}>
                <div style={{
                  width: 24, height: 24, borderRadius: 12,
                  background: status === ApprovalStageStatus.Approved ? '#16A34A'
                    : status === ApprovalStageStatus.Rejected ? '#DC2626'
                    : status === ApprovalStageStatus.Skipped ? '#9198A8'
                    : '#F3F4F6',
                  color: status === ApprovalStageStatus.Pending ? '#5A6272' : '#fff',
                  fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 2,
                }}>{s.dagrun_pm_stageorder ?? '–'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#0F1117', fontWeight: 500 }}>{s.dagrun_pm_name}</div>
                  <div style={{ fontSize: 11, color: '#5A6272', marginTop: 2 }}>
                    {s.dagrun_pm_approvername ?? s.dagrun_pm_approverid ?? '—'}
                    {s.dagrun_pm_decidedon && (
                      <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)', color: '#9198A8' }}>
                        {new Date(s.dagrun_pm_decidedon).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {s.dagrun_pm_comments && (
                    <div style={{ fontSize: 12, color: '#5A6272', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                      {s.dagrun_pm_comments}
                    </div>
                  )}
                  {isMyStage && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button type="button" onClick={() => setApprovingId(s.dagrun_pm_approvalstageid)}
                        style={{ background: '#16A34A', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >Approve</button>
                      <button type="button" onClick={() => setRejectingId(s.dagrun_pm_approvalstageid)}
                        style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >Reject</button>
                    </div>
                  )}
                  {approvingId === s.dagrun_pm_approvalstageid && (
                    <div style={{ marginTop: 8, padding: 8, background: '#fff', borderRadius: 4 }}>
                      <div style={{ fontSize: 11, color: '#5A6272', marginBottom: 4 }}>Optional approval comments</div>
                      <textarea value={approveComments} onChange={e => setApproveComments(e.target.value)}
                        style={{ width: '100%', minHeight: 48, fontSize: 12 }} />
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                        <button type="button" onClick={() => { setApprovingId(null); setApproveComments(''); }}
                          style={{ background: 'transparent', border: '1px solid #DDE0E6', color: '#5A6272', padding: '3px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer' }}
                        >Cancel</button>
                        <button type="button" onClick={async () => {
                            await onDecision(s, 'approve', approveComments.trim() || undefined);
                            setApprovingId(null); setApproveComments('');
                          }}
                          style={{ background: '#16A34A', color: '#fff', border: 'none', padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: 'pointer' }}
                        >Confirm Approve</button>
                      </div>
                    </div>
                  )}
                  {rejectingId === s.dagrun_pm_approvalstageid && (
                    <div style={{ marginTop: 8, padding: 8, background: '#fff', borderRadius: 4 }}>
                      <div style={{ fontSize: 11, color: '#5A6272', marginBottom: 4 }}>Reason for rejection (required)</div>
                      <textarea value={rejectComments} onChange={e => setRejectComments(e.target.value)}
                        style={{ width: '100%', minHeight: 48, fontSize: 12 }} />
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                        <button type="button" onClick={() => { setRejectingId(null); setRejectComments(''); }}
                          style={{ background: 'transparent', border: '1px solid #DDE0E6', color: '#5A6272', padding: '3px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer' }}
                        >Cancel</button>
                        <button type="button"
                          onClick={async () => {
                            if (!rejectComments.trim()) return;
                            await onDecision(s, 'reject', rejectComments.trim());
                            setRejectingId(null); setRejectComments('');
                          }}
                          disabled={!rejectComments.trim()}
                          style={{ background: '#DC2626', color: '#fff', border: 'none', padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: rejectComments.trim() ? 'pointer' : 'not-allowed', opacity: rejectComments.trim() ? 1 : 0.6 }}
                        >Confirm Reject</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
